import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Step, StepList } from '@apigrowfinance/pbolt-stepper';
// eslint-disable-next-line import/no-unresolved
import { Notify } from 'notify/services/Notify';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AccountDetails } from '@clients/accountsClient';
import {
    AccountFlexControlsFormInputs,
    CustomerFlexControlsFormInputs,
} from '@clients/domains/flexControls/flexControlsClient';
import usePostCreateCustomerFlexControl from '@hooks/queries/usePostCreateCustomerFlexControl';
import CreateFlexControlSchema from '@pages/flexControls/createFlexControl/createFlexControlSchema';
import Steps from '@pages/flexControls/createFlexControl/enums';
import BasicDetails from '@pages/flexControls/createFlexControl/formSteps/BasicDetails';
import Conditions from '@pages/flexControls/createFlexControl/formSteps/Conditions';
import ResetPeriod from '@pages/flexControls/createFlexControl/formSteps/ResetPeriod';
import { formatCustomerFlexControlInputs } from '@utils/formatFlexControlInputs';

import { attributes, operators } from '../createFlexControl/fieldParameters';

interface CustomerFlexControlsStepsProps {
    stepperStage: Steps;
    setStepperStage: React.Dispatch<React.SetStateAction<number>>;
    customerId: number;
    customerName: string;
    account: AccountDetails;
}
const notify = Notify.getInstance();

const CustomerFlexControlSteps = ({
    account,
    customerName,
    stepperStage,
    setStepperStage,
    customerId,
}: CustomerFlexControlsStepsProps) => {
    const { formatMessage } = useIntl();
    const currentValidationSchema = CreateFlexControlSchema[stepperStage];
    const navigate = useNavigate();

    const methods = useForm({
        resolver: yupResolver(currentValidationSchema),
        shouldUnregister: false,
        mode: 'onChange',
        defaultValues: {
            active: true,
        } as AccountFlexControlsFormInputs,
    });
    const { handleSubmit, trigger } = methods;

    const stepForward = async () => {
        const isStepValid = await trigger();
        if (isStepValid && stepperStage < Steps.Conditions) {
            setStepperStage(stepperStage + 1);
        }
    };
    const stepBack = () => {
        if (stepperStage > Steps.BasicDetails) {
            setStepperStage(stepperStage - 1);
        }
    };

    const {
        data,
        isLoading: isSubmitting,
        mutate,
    } = usePostCreateCustomerFlexControl(
        customerId,
        account?.account_id,
        () => {
            notify.addNotification(
                formatMessage(
                    {
                        id: 'CustomerFlexControlSteps.success',
                        defaultMessage:
                            'You’ve successfully added the customer control {flexControlName} to {accountName}. You can find it at the bottom of the customer list below.',
                    },
                    {
                        flexControlName: data?.name,
                        accountName: customerName,
                    },
                ),
            );
            navigate(`/${account?.account_id}/detail/controls`);
        },
    );

    const onSubmit = (data: CustomerFlexControlsFormInputs) => {
        data.account_id = account?.account_id;
        const dataToMutate = formatCustomerFlexControlInputs(data);
        mutate(dataToMutate);
    };

    return (
        <FormProvider {...methods}>
            <form>
                <StepList
                    currentStep={stepperStage}
                    stepForward={stepForward}
                    stepBack={stepBack}
                    submit={handleSubmit(onSubmit)}>
                    <Step>
                        <BasicDetails />
                    </Step>
                    <Step>
                        <ResetPeriod />
                    </Step>
                    <Step>
                        <Conditions
                            isSubmitting={isSubmitting}
                            attributes={attributes}
                            operators={operators}
                        />
                    </Step>
                </StepList>
            </form>
        </FormProvider>
    );
};

export default CustomerFlexControlSteps;
